import { DEFAULT_SAFE_LIST_SORT, SAFE_LIST_SORT_ORDERS } from '@heybesafe/shared';
import type { SafeListSortOrder } from '@heybesafe/shared';
/**
 * localStorage-based vault metadata store.
 * Mirrors the extension's IndexedDB metadata store using localStorage keys.
 * Vault items are NOT stored here — they live in memory only (SafeContext).
 */
import type { CategorySummary } from '@heybesafe/shared';
import type { BiometricEnrollment } from '../crypto/biometricUnlock';

/**
 * Composite cache key for in-memory vault items.
 *
 * Phase A: server `/pull` now aggregates items across the user's personal
 * partition AND every org partition they belong to. A bare item `id` is only
 * unique within a single partition; an item-id collision between two
 * partitions (rare but legal — UUIDs are not enforced server-side as globally
 * unique) would make `items.find(i => i.id === id)` and the dedup logic in
 * `syncPull` ambiguous. Key items by `(orgId ?? 'self', id)` so two items
 * with the same id but different orgIds round-trip independently.
 */
export function cacheKey(orgId: string | null | undefined, itemId: string): string {
  return `${orgId ?? 'self'}:${itemId}`;
}

type MetaKey =
  | 'email'
  | 'localKdfSalt'
  | 'localWrappedSafeKey'
  | 'localWrappedSafeKeyIv'
  | 'lastSyncedAt';

export const KEYS: Record<MetaKey, string> = {
  email:                  'hbs:email',
  localKdfSalt:           'hbs:localKdfSalt',
  localWrappedSafeKey:    'hbs:localWrappedSafeKey',
  localWrappedSafeKeyIv:  'hbs:localWrappedSafeKeyIv',
  lastSyncedAt:           'hbs:lastSyncedAt',
};

export function getMeta(key: MetaKey): string | null {
  return localStorage.getItem(KEYS[key]);
}

export function setMeta(key: MetaKey, value: string): void {
  localStorage.setItem(KEYS[key], value);
}

export function removeMeta(key: MetaKey): void {
  localStorage.removeItem(KEYS[key]);
}

// Per-browser UI preferences that are NOT user data and must survive logout.
// The welcome banner's "seen" flag lives under the `hbs:` prefix, so without
// this exclusion the blanket sweep below would wipe it on every logout and the
// banner would reappear after each re-login. See components/WelcomeBanner.tsx.
// hbs:hasAccount must survive logout: it is the only remaining signal that this
// browser has an account, and without it a user who explicitly logged out would
// be shown "Create your safe" on their next visit. It is a bare boolean — no
// email, no PII — precisely so preserving it is safe on a shared computer.
const PRESERVE_ON_LOGOUT = new Set<string>(['hbs:seenWelcomeBanner', 'hbs:hasAccount']);

export function clearAllLocalData(): void {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('hbs:') && !PRESERVE_ON_LOGOUT.has(key)) keys.push(key);
  }
  for (const key of keys) {
    localStorage.removeItem(key);
  }
}

// ─── Account-level X25519 privkey local wrap helpers (Phase 3) ───────────────
// Phase 3: account-level X25519 privkey, locally wrapped under the same
// PBKDF2-derived localWrappingKey that wraps the vault key.
// clearAllLocalData() above scans every localStorage key with the `hbs:`
// prefix, so these keys are wiped on logout alongside the vault key.

export function setLocalWrappedAccountKey(ciphertext: string, iv: string): void {
  localStorage.setItem('hbs:localWrappedAccountKey', ciphertext);
  localStorage.setItem('hbs:localWrappedAccountKeyIv', iv);
}

// PKA: local wrap of the Ed25519 identity private key, mirroring the account-key
// local wrap so unlock() can repopulate it without a full OPAQUE re-login.
export function setLocalWrappedIdentityKey(ciphertext: string, iv: string): void {
  localStorage.setItem('hbs:localWrappedIdentityKey', ciphertext);
  localStorage.setItem('hbs:localWrappedIdentityKeyIv', iv);
}

// ─── Family Binder category cache (stale-while-revalidate) ───────────────────
// Per-family cache of the server's *encrypted* CategorySummary list. These
// blobs are the same zero-knowledge ciphertext the server holds (encrypted
// name/iv/ephemeral pubkey + plaintext categoryId/circleId/order), so caching
// them client-side leaks nothing at rest — no decrypted name is ever persisted.
// FamilyCategoriesProvider seeds the rail from this cache on unlock (decrypting
// locally, no network) and revalidates in the background. Wiped on logout by
// clearAllLocalData() via the `hbs:` prefix scan.

const familyCategoriesKey = (orgId: string): string => `hbs:familyCategories:${orgId}`;

export function getCachedCategories(orgId: string): CategorySummary[] | null {
  const raw = localStorage.getItem(familyCategoriesKey(orgId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CategorySummary[]) : null;
  } catch {
    return null;
  }
}

export function setCachedCategories(orgId: string, summaries: CategorySummary[]): void {
  localStorage.setItem(familyCategoriesKey(orgId), JSON.stringify(summaries));
}

// ─── Safe-list sort preference ───────────────────────────────────────────────
// Which order the safe list is shown in. A UI preference, not secret data, but
// it carries the `hbs:` prefix so clearAllLocalData() wipes it on logout like
// everything else — a sort order is a small signal about how someone uses their
// safe, and there is no reason for it to outlive the session on a shared device.

const SAFE_LIST_SORT_KEY = 'hbs:safeListSort';

export function getSafeListSort(): SafeListSortOrder {
  const raw = localStorage.getItem(SAFE_LIST_SORT_KEY);
  // Validate against the known set — a hand-edited or stale value must fall
  // back rather than produce an unsorted list.
  return SAFE_LIST_SORT_ORDERS.includes(raw as SafeListSortOrder)
    ? (raw as SafeListSortOrder)
    : DEFAULT_SAFE_LIST_SORT;
}

export function setSafeListSort(order: SafeListSortOrder): void {
  localStorage.setItem(SAFE_LIST_SORT_KEY, order);
}

// ─── Family member display-name cache (stale-while-revalidate) ───────────────
// Per-family cache of each member's *encrypted* display-name blob — the same
// zero-knowledge ciphertext the server holds, so caching it leaks nothing at
// rest and no decrypted name is ever persisted (same property as the category
// cache above). Exists because MyFamilyNameCard and the member rosters would
// otherwise paint blank/email on every visit to a family page and fill in only
// after a getOrg round-trip, which reads as the UI correcting itself.
// Wiped on logout by clearAllLocalData() via the `hbs:` prefix scan.

/** One member's encrypted name blob, as cached. Mirrors the three fields
 *  `OrgMember` carries; `userId` keys them back to the roster. */
export interface CachedMemberName {
  userId: string;
  encryptedName?: string;
  encryptedNameIv?: string;
  encryptedNameEphemeralPublicKey?: string;
}

const familyMemberNamesKey = (orgId: string): string => `hbs:familyMemberNames:${orgId}`;

export function getCachedMemberNames(orgId: string): CachedMemberName[] | null {
  const raw = localStorage.getItem(familyMemberNamesKey(orgId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CachedMemberName[]) : null;
  } catch {
    return null;
  }
}

export function setCachedMemberNames(orgId: string, members: CachedMemberName[]): void {
  // Store only the four fields we need — never the whole OrgMember, which
  // carries email, role and key material that has no business in this cache.
  const slim: CachedMemberName[] = members.map((m) => ({
    userId: m.userId,
    encryptedName: m.encryptedName,
    encryptedNameIv: m.encryptedNameIv,
    encryptedNameEphemeralPublicKey: m.encryptedNameEphemeralPublicKey,
  }));
  localStorage.setItem(familyMemberNamesKey(orgId), JSON.stringify(slim));
}

// ─── Biometric unlock (WebAuthn PRF) enrollment ──────────────────────────────
// The safe password wrapped behind a platform-authenticator PRF key (see
// web/src/crypto/biometricUnlock.ts). All four keys carry the `hbs:` prefix, so
// clearAllLocalData() wipes them on logout. Ciphertext only — the key is
// re-derived from a live biometric match, never persisted.

const BIO_KEYS = {
  credentialId:      'hbs:bioCredentialId',
  prfSalt:           'hbs:bioPrfSalt',
  wrappedPassword:   'hbs:bioWrappedPassword',
  wrappedPasswordIv: 'hbs:bioWrappedPasswordIv',
} as const;

export function getBiometricEnrollment(): BiometricEnrollment | null {
  const credentialId = localStorage.getItem(BIO_KEYS.credentialId);
  const prfSalt = localStorage.getItem(BIO_KEYS.prfSalt);
  const wrappedPassword = localStorage.getItem(BIO_KEYS.wrappedPassword);
  const wrappedPasswordIv = localStorage.getItem(BIO_KEYS.wrappedPasswordIv);
  if (!credentialId || !prfSalt || !wrappedPassword || !wrappedPasswordIv) return null;
  return { credentialId, prfSalt, wrappedPassword, wrappedPasswordIv };
}

export function setBiometricEnrollment(e: BiometricEnrollment): void {
  localStorage.setItem(BIO_KEYS.credentialId, e.credentialId);
  localStorage.setItem(BIO_KEYS.prfSalt, e.prfSalt);
  localStorage.setItem(BIO_KEYS.wrappedPassword, e.wrappedPassword);
  localStorage.setItem(BIO_KEYS.wrappedPasswordIv, e.wrappedPasswordIv);
}

export function clearBiometricEnrollment(): void {
  for (const key of Object.values(BIO_KEYS)) localStorage.removeItem(key);
}

export function getLocalWrappedAccountKey(): { ciphertext: string; iv: string } | null {
  const ciphertext = localStorage.getItem('hbs:localWrappedAccountKey');
  const iv = localStorage.getItem('hbs:localWrappedAccountKeyIv');
  if (!ciphertext || !iv) return null;
  return { ciphertext, iv };
}

export function removeLocalWrappedAccountKey(): void {
  localStorage.removeItem('hbs:localWrappedAccountKey');
  localStorage.removeItem('hbs:localWrappedAccountKeyIv');
}

export function getLocalWrappedIdentityKey(): { ciphertext: string; iv: string } | null {
  const ciphertext = localStorage.getItem('hbs:localWrappedIdentityKey');
  const iv = localStorage.getItem('hbs:localWrappedIdentityKeyIv');
  if (!ciphertext || !iv) return null;
  return { ciphertext, iv };
}

export function removeLocalWrappedIdentityKey(): void {
  localStorage.removeItem('hbs:localWrappedIdentityKey');
  localStorage.removeItem('hbs:localWrappedIdentityKeyIv');
}
